import type { ForwardedRef, HTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';
import type { DefaultTheme, StyledComponent } from 'styled-components';
import styled from 'styled-components';
import { variant } from 'styled-system';
import type { ITheme } from '../../utils/theme';
import { TypographyVariant } from '../../utils/theme';

const headingRoles = new Set([
  TypographyVariant.h1,
  TypographyVariant.h2,
  TypographyVariant.h3,
]);

export interface TypographyProps extends HTMLAttributes<HTMLParagraphElement> {
  variant?: TypographyVariant;
  children?: ReactNode;
  color?: string;
  fontWeight?: number;
  as?:
    | TypographyVariant.h1
    | TypographyVariant.h2
    | TypographyVariant.h3
    | 'span'
    | 'div'
    | 'h4'
    | 'dt'
    | 'dd'
    | 'p';
}

export const Typography = forwardRef(
  (
    {
      variant = TypographyVariant.h1,
      children,
      color,
      fontWeight,
      ...rest
    }: TypographyProps,
    ref: ForwardedRef<HTMLParagraphElement>
  ) => {
    return (
      <StyledTypography
        ref={ref}
        variant={variant}
        color={color}
        fontWeight={fontWeight}
        as={headingRoles.has(variant) ? variant : undefined}
        {...rest}
      >
        {children}
      </StyledTypography>
    );
  }
);

Typography.displayName = 'Typography';

export default Typography;

const variantStyles = ({
  color,
  fontWeight,
  theme,
}: {
  theme: ITheme;
  color: Pick<TypographyProps, 'color'>;
  fontWeight: Pick<TypographyProps, 'fontWeight'>;
}) => {
  const variantObject = Object.fromEntries(
    Object.keys(TypographyVariant).map((key) => [
      key,
      getTypographyStyles(theme, key as TypographyVariant, color, fontWeight),
    ])
  );

  return variant({
    variants: variantObject,
  });
};

const StyledTypography: StyledComponent<
  'p',
  DefaultTheme,
  Record<string, unknown>,
  never
> = styled('p')(variantStyles);

const getTypographyStyles = (
  theme: ITheme,
  variant: TypographyVariant,
  color?: Pick<TypographyProps, 'color'>,
  fontWeight?: Pick<TypographyProps, 'fontWeight'>
) => {
  let variantStyles = theme.typography[variant];

  variantStyles = {
    ...variantStyles,
    color: (color as string) || theme.palette.neutral[100],
  };

  if (fontWeight) {
    variantStyles = { ...variantStyles, fontWeight: fontWeight as number };
  }
  return variantStyles;
};
